import type { JSX, ReactNode } from 'react';
import { useCallback, useRef, useState } from 'react';
import { Menu } from '@mantine/core';

/**
 * Right-click menus.
 *
 * `useContextMenu` gives an `open` handler for onContextMenu and the menu to
 * render once per page. The menu opens where the pointer is; Escape, a click
 * outside or choosing an entry closes it. Every entry in a context menu is
 * also reachable without one - a button, the ⋯ menu or the toolbar - so
 * nobody depends on the right mouse button. `openAt` opens it at a point of
 * the caller's choosing (the context-menu key, a long press on a phone), and
 * `onClose` hears when it goes, e.g. to give the focus back.
 */
export function useContextMenu<T>(
  render: (item: T, close: () => void) => ReactNode,
  options: { onClose?: (item: T) => void } = {},
): {
  open: (event: React.MouseEvent, item: T) => void;
  openAt: (x: number, y: number, item: T) => void;
  menu: JSX.Element | null;
} {
  const [state, setState] = useState<{ x: number; y: number; item: T } | null>(null);
  const current = useRef(state);
  current.current = state;
  const onClose = useRef(options.onClose);
  onClose.current = options.onClose;
  const close = useCallback(() => {
    const was = current.current;
    current.current = null;
    setState(null);
    if (was) onClose.current?.(was.item);
  }, []);
  const open = useCallback((event: React.MouseEvent, item: T) => {
    // In a text field the browser's own menu (paste, spelling) stays.
    const target = event.target as HTMLElement | null;
    if (target?.closest('input, textarea, select, [contenteditable="true"]')) return;
    event.preventDefault();
    event.stopPropagation();
    setState({ x: event.clientX, y: event.clientY, item });
  }, []);
  const openAt = useCallback((x: number, y: number, item: T) => setState({ x, y, item }), []);

  const menu = state ? (
    <Menu opened onChange={(opened) => !opened && close()} position="bottom-start" offset={2} withinPortal shadow="md" width={240}>
      <Menu.Target>
        <div style={{ position: 'fixed', left: state.x, top: state.y, width: 1, height: 1 }} />
      </Menu.Target>
      <Menu.Dropdown onContextMenu={(event) => event.preventDefault()}>{render(state.item, close)}</Menu.Dropdown>
    </Menu>
  ) : null;

  return { open, openAt, menu };
}
